import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2019 semantics GmbH
//  Date      : $Date: 2026-01-14 17:43:36 +0100 (Wed, 14 Jan 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 159808 $
//  Purpose   : react components for auth system
// =============================================================================
// ---Third-party imports
import React, { useEffect, useState } from 'react';
import { Button, Step, Segment, Grid, Table, Icon, Menu, Card, Checkbox } from 'semantic-ui-react';
// ---Framework imports
import { Actions, Sel } from 'uix/registries';
import { VIcon } from 'uix/graphics';
import log from 'uix/logging';
import { get, isNil, strip, size } from 'ldash';
import connect from 'uix/connect';
import { useActions, useSelector, useUserSetting } from 'uix/hooks';
import { applyProps } from 'uix/components/utils';
import { DebugFeature, XPopup, Dialog } from 'uix/components';
import { formatDateTime } from 'uix/utils';
// ---Local imports
import { beginWebauthn, loginWebauthn, queryWebauthn } from './actions';
import { useUserData } from './hooks';
import { checkAcl } from './acl';
import { XMenu } from '../components';
// ---Globals
let REGISTRATION_STEPS = [
    // step 1 - initialize registration options
    {
        label: 'Create Options',
        active: 'REG_BEGIN',
        completed: [
            'REG_OPTIONS_CREATED',
            'REG_COMPLETE'
        ],
        failure: [
            'ERROR_REG_BEGIN'
        ]
    },
    // step 2 - create and verify credentials
    {
        label: 'Verify Registration',
        active: 'REG_OPTIONS_CREATED',
        completed: [
            'REG_COMPLETE'
        ],
        failure: [
            'ERROR_REG_CREDENTIALS_CREATE',
            'ERROR_REG_CREDENTIALS_VERIFY'
        ]
    }
];
let LOGIN_STEPS = [
    // step 1 - initialize login options
    {
        label: 'Create Options',
        active: 'LOGIN_BEGIN',
        completed: [
            'LOGIN_OPTIONS_CREATED',
            'LOGIN_COMPLETE'
        ],
        failure: [
            'ERROR_LOGIN_BEGIN'
        ]
    },
    // step 2 - create and verify credentials
    {
        label: 'Verify Credentials',
        active: 'REG_OPTIONS_CREATED',
        completed: [
            'LOGIN_COMPLETE'
        ],
        failure: [
            'ERROR_LOGIN_CREDENTIALS_CREATE',
            'ERROR_LOGIN_CREDENTIALS_VERIFY'
        ]
    }
];
export const WebAuthNDiag = connect('WebAuthN', {
    storeMap: [
        'meta.user',
        'meta.webauthn'
    ],
    actionMap: {
        beginWebauthn,
        loginWebauthn,
        queryWebauthn
    }
}, ({ user, beginWebauthn, loginWebauthn, queryWebauthn, webauthn, callApi })=>{
    useEffect(()=>{
        if (isNil(webauthn.available)) {
            queryWebauthn(user.name);
        }
    }, []);
    let [keys, setKeys] = useState([]);
    useEffect(()=>{
        callApi('webauthn/keys', (data, error)=>setKeys(data.keys));
    }, [
        webauthn
    ]);
    return /*#__PURE__*/ _jsxs(Grid, {
        columns: 2,
        children: [
            /*#__PURE__*/ _jsxs(Grid.Column, {
                width: 6,
                children: [
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                size: "huge",
                                onClick: ()=>beginWebauthn(user),
                                content: "Register"
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                size: "huge",
                                onClick: ()=>beginWebauthn(user, {
                                        reset: true
                                    }),
                                content: "Reset"
                            }),
                            /*#__PURE__*/ _jsx(Step.Group, {
                                ordered: true,
                                size: "tiny",
                                children: REGISTRATION_STEPS.map((r, i)=>/*#__PURE__*/ _jsx(Step, {
                                        completed: r.completed.includes(webauthn.state),
                                        content: r.label,
                                        active: webauthn.state == r.active
                                    }, i))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                disabled: !Boolean(webauthn.available),
                                size: "huge",
                                onClick: ()=>loginWebauthn(user.name),
                                content: `Login with ${user.name}`
                            }),
                            /*#__PURE__*/ _jsx(Step.Group, {
                                ordered: true,
                                size: "tiny",
                                children: LOGIN_STEPS.map((r, i)=>/*#__PURE__*/ _jsx(Step, {
                                        completed: r.completed.includes(webauthn.state),
                                        content: r.label,
                                        active: webauthn.state == r.active
                                    }, i))
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs(Segment, {
                        children: [
                            /*#__PURE__*/ _jsx("tt", {
                                children: webauthn.state
                            }),
                            webauthn.error ? /*#__PURE__*/ _jsxs("tt", {
                                children: [
                                    " ",
                                    get(webauthn.error, 'errorMsg')
                                ]
                            }) : null
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Grid.Column, {
                width: 10,
                children: /*#__PURE__*/ _jsxs(Table, {
                    definition: true,
                    children: [
                        /*#__PURE__*/ _jsx(Table.Header, {
                            children: /*#__PURE__*/ _jsxs(Table.Row, {
                                children: [
                                    /*#__PURE__*/ _jsx(Table.HeaderCell, {}),
                                    keys.map((k, i)=>/*#__PURE__*/ _jsx(Table.HeaderCell, {
                                            children: `Key ${i}`
                                        }, k.cred_id))
                                ]
                            })
                        }),
                        /*#__PURE__*/ _jsxs(Table.Body, {
                            children: [
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "ID"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: k.cred_id.slice(0, 20)
                                            }, k.cred_id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "Date"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: formatDateTime(k.created_dt)
                                            }, k.cred_id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "PubKey"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: k.pub_key.slice(0, 20)
                                            }, k.cred_id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "RP-ID"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: k.rp_id
                                            }, k.cred_id))
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(Table.Row, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Table.Cell, {
                                            children: "UKey"
                                        }),
                                        keys.map((k)=>/*#__PURE__*/ _jsx(Table.Cell, {
                                                children: k.ukey
                                            }, k.cred_id))
                                    ]
                                })
                            ]
                        })
                    ]
                })
            })
        ]
    });
});
export const UserInfo = ({ style, puid, title, shortenInitials = false })=>{
    /* a user name with an icon
   */ puid = parseInt(puid);
    let curr_user = useSelector(Sel.user);
    let runImpersonify = useActions('runImpersonify');
    let user = useUserData(puid);
    let content = /*#__PURE__*/ _jsx(Icon, {
        style: style,
        name: "user outline",
        color: "grey",
        title: `puid: ${puid}`
    });
    if (user) {
        // non admin lookups emit only {puid, fullname}
        let name = user.fullname || user.login || '';
        if (shortenInitials) {
            let sep = name.includes('.') ? '.' : ' ';
            let initials = name.split(sep).map((n)=>strip(n)[0]).filter((e)=>e);
            if (!title) {
                title = name;
            }
            // no short name for server user type
            name = user.type != 10 ? initials.map((s)=>`${s}.`).join(' ') : '';
        }
        let buildIcon = (type)=>{
            if (type == 10) {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: "server",
                    color: "grey"
                });
            } else {
                return /*#__PURE__*/ _jsx(Icon, {
                    name: "user",
                    color: "grey"
                });
            }
        };
        // handle debug user impersonify
        let onClick = null;
        let styles = {
            paddingLeft: '2px',
            ...style
        };
        if (user.login && curr_user.hasGroup('debug')) {
            onClick = ()=>runImpersonify(user);
            styles = {
                ...styles,
                cursor: 'pointer'
            };
        }
        return /*#__PURE__*/ _jsxs("span", {
            style: styles,
            title: title || `puid: ${puid}`,
            onClick: onClick,
            children: [
                buildIcon(user.type),
                /*#__PURE__*/ _jsx("span", {
                    children: name
                })
            ]
        });
    }
    return content;
};
export const LogoutMenuItem = ()=>{
    let runLogout = useActions(Actions.runLogout);
    return /*#__PURE__*/ _jsx(Menu.Item, {
        onClick: runLogout,
        title: "Logout",
        icon: "sign out"
    });
};
const ResetPartPersistence = ({})=>{
    let clearPartPersistence = useActions('clearPartPersistence');
    let request = useSelector(Sel.request);
    let classCfg = useSelector(Sel.classCfg);
    let parts = [
        ...classCfg.parts,
        ...classCfg.tabs[request.tabname].parts
    ];
    parts = parts.map((p, i)=>{
        let n = p.name || p.type;
        return /*#__PURE__*/ _jsx("div", {
            children: /*#__PURE__*/ _jsx(Button, {
                fluid: true,
                size: "tiny",
                basic: true,
                compact: true,
                icon: "history",
                content: `Reset ${n}`,
                onClick: ()=>clearPartPersistence(n)
            })
        }, `${n}_${i}`);
    });
    return /*#__PURE__*/ _jsx("div", {
        children: parts
    });
};
const GlobalUserSettings = ()=>{
    /* custom user settings
  using persistence via store.root.ui.byId['Global/*']
  */ let clearLocalStorage = useActions(Actions.clearLocalStorage);
    let switchTheme = useActions(Actions.switchTheme);
    let theme = useSelector((root)=>root.meta.theme.name);
    let [colorizeDomains, setColorizeDomains] = useUserSetting('colorizeDomains');
    let [headerStyle, setHeaderStyle] = useUserSetting('headerStyle');
    let [navBarTarget, setNavBarTarget] = useUserSetting('navBarTarget');
    const NavBarButtons = ()=>/*#__PURE__*/ _jsxs("div", {
            style: {
                paddingBottom: '0.5rem'
            },
            children: [
                /*#__PURE__*/ _jsxs(Button.Group, {
                    style: {
                        paddingRight: '0.5rem'
                    },
                    size: "mini",
                    label: "position",
                    labeled: true,
                    color: "grey",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'leftPortal',
                            onClick: ()=>setNavBarTarget('leftPortal'),
                            icon: "chevron left"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'topPortal',
                            onClick: ()=>setNavBarTarget('topPortal'),
                            icon: "chevron up"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerLeftPortal',
                            onClick: ()=>setNavBarTarget('centerLeftPortal'),
                            icon: "circle outline"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerBottomPortal',
                            onClick: ()=>setNavBarTarget('centerBottomPortal'),
                            icon: "chevron down"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            active: navBarTarget === 'centerRightPortal',
                            onClick: ()=>setNavBarTarget('centerRightPortal'),
                            icon: "chevron right"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("span", {
                    style: {
                        fontSize: 'var(--vl_bodySecondary_fontSize)'
                    },
                    children: "Position der Navigation"
                })
            ]
        });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: colorizeDomains,
                label: "Colorize Domains",
                onClick: ()=>setColorizeDomains(!colorizeDomains)
            }),
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: headerStyle === 'compact',
                label: "Compact Header",
                onClick: ()=>setHeaderStyle(headerStyle === 'default' ? 'compact' : 'default')
            }),
            /*#__PURE__*/ _jsx(Checkbox, {
                style: {
                    marginBottom: '0.5rem'
                },
                toggle: true,
                checked: theme === 'dark',
                label: "Dark Mode",
                onClick: ()=>switchTheme(theme === 'dark' ? 'default' : 'dark')
            }),
            /*#__PURE__*/ _jsx(AclFeature, {
                acl: "g:admin",
                children: /*#__PURE__*/ _jsx(NavBarButtons, {})
            }),
            /*#__PURE__*/ _jsx(Button, {
                color: "grey",
                icon: "history",
                size: "tiny",
                fluid: true,
                content: "Reset all Local Settings",
                onClick: clearLocalStorage
            }),
            /*#__PURE__*/ _jsx(ResetPartPersistence, {})
        ]
    });
};
export const ProfileMenuItem = ()=>{
    let [openPopup, setOpenPopup] = useState(false);
    let [settings, setSettings] = useState(false);
    let user = useSelector(Sel.user);
    let userData = useUserData(user.puid);
    let runLogout = useActions(Actions.runLogout);
    const settingHandle = ()=>setSettings(!settings);
    if (!userData) {
        return /*#__PURE__*/ _jsx(Menu.Item, {
            icon: "user circle outline"
        });
    }
    return /*#__PURE__*/ _jsx(_Fragment, {
        children: /*#__PURE__*/ _jsx(XPopup, {
            on: "click",
            basic: true,
            fitted: true,
            position: "bottom right",
            onOpen: ()=>setOpenPopup(!openPopup),
            onClose: ()=>setOpenPopup(!openPopup),
            trigger: /*#__PURE__*/ _jsx(Menu.Item, {
                title: "User Menü",
                icon: "user circle outline",
                active: openPopup
            }),
            children: /*#__PURE__*/ _jsxs(Card, {
                children: [
                    /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsxs("div", {
                            style: {
                                display: 'flex',
                                flexDirection: 'row',
                                alignItems: 'center',
                                verticalAlign: 'middle',
                                justifyContent: 'space-between'
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    style: {
                                        padding: '0 0.5rem'
                                    },
                                    children: [
                                        user.fullname || user.name,
                                        /*#__PURE__*/ _jsx(Card.Meta, {
                                            children: userData.email
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx(XMenu, {
                                    style: {
                                        margin: 0
                                    },
                                    secondary: true,
                                    children: /*#__PURE__*/ _jsx(XMenu.Item, {
                                        active: settings,
                                        icon: "cog",
                                        onClick: settingHandle
                                    })
                                })
                            ]
                        })
                    }),
                    settings && /*#__PURE__*/ _jsx(Card.Content, {
                        children: /*#__PURE__*/ _jsx(GlobalUserSettings, {})
                    }),
                    /*#__PURE__*/ _jsx(Card.Content, {
                        textAlign: "center",
                        extra: true,
                        children: /*#__PURE__*/ _jsxs(Button, {
                            size: "small",
                            animated: "fade",
                            onClick: runLogout,
                            children: [
                                /*#__PURE__*/ _jsx(Button.Content, {
                                    hidden: true,
                                    children: /*#__PURE__*/ _jsx(VIcon, {
                                        name: "sign-out"
                                    })
                                }),
                                /*#__PURE__*/ _jsx(Button.Content, {
                                    visible: true,
                                    children: "Logout"
                                })
                            ]
                        })
                    })
                ]
            })
        })
    });
};
export const UserMenuItem = ()=>{
    let setContextHelpOn = useActions(Actions.setContextHelpOn);
    let contextHelpOn = useSelector((root)=>root.meta.contextHelp.on);
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Menu.Item, {
                onClick: ()=>setContextHelpOn(!contextHelpOn),
                icon: "question circle outline",
                title: "Hilfe",
                active: contextHelpOn
            }),
            /*#__PURE__*/ _jsx(ProfileMenuItem, {})
        ]
    });
};
export const AclFeature = ({ acl, fallback, children, ...props })=>{
    /* render children if acl check passes
   */ let user = useSelector(Sel.user);
    let { name: dname } = useSelector(Sel.domain);
    if (!checkAcl(acl, user, dname)) {
        if (fallback) {
            return fallback;
        }
        return null;
    }
    // a AclFeature in a XMenu needs props pass through
    return size(props) ? applyProps(children, props) : children;
};
